import React, { useMemo } from "react";
import { useHSE } from "../lib/store";
import SiteMap from "../components/SiteMap";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  RadialBarChart, RadialBar,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend, ComposedChart
} from "recharts";
import {
  Shield, ShieldCheck, Bandage, AlertTriangle, MessageSquareWarning, Leaf, ClipboardList, ClipboardCheck, GraduationCap, FileBadge,
  TrendingUp, TrendingDown, Minus, ArrowRight, ChevronRight, Building2, ShieldAlert, Eye, FileX2,
  CalendarX, MapPin, Clock4, AlertCircle, Wind, Recycle, Zap, Users
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const NAVY = "#082B4D";
const GREEN = "#18B84F";
const AMBER = "#F59E0B";
const RED = "#EF4444";
const BLUE = "#2563EB";
const GRAY = "#6B7280";
const PURPLE = "#8B5CF6";
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function ExecutiveDashboard() {
  const { data, filters, siteNames } = useHSE();
  const nav = useNavigate();

  const sites = useMemo(() => {
    const siteMaster = data.settings?.sites || [];
    if (filters.site !== "All Sites") return [filters.site];
    if (filters.region === "All Regions") return siteNames;
    return siteMaster
      .filter((s) => typeof s === "object" && s.region === filters.region)
      .map((s) => s.number || s.name);
  }, [filters.site, filters.region, siteNames, data.settings?.sites]);

  const totals = useMemo(() => {
    const sumKey = (k) => sites.reduce((acc, s) => {
      const arr = data.monthlyKPI[s]?.[k] || [];
      return acc.map((v, i) => v + (arr[i] || 0));
    }, MONTHS.map(() => 0));
    return {
      manpower: sumKey("manpower"), manhours: sumKey("manhours"),
      fac: sumKey("fac"), mtc: sumKey("mtc"), lti: sumKey("lti"),
      workdaysLost: sumKey("workdaysLost"), incidents: sumKey("incidents"),
      nearMiss: sumKey("nearMiss"), propertyDamage: sumKey("propertyDamage"),
      violations: sumKey("violations"),
      obsRaised: sumKey("obsRaised"), obsClosed: sumKey("obsClosed"),
      sorReceived: sumKey("sorReceived"), sorOpen: sumKey("sorOpen"), sorClosed: sumKey("sorClosed"),
      ncrReceived: sumKey("ncrReceived"), ncrOpen: sumKey("ncrOpen"), ncrClosed: sumKey("ncrClosed"),
      training: sumKey("training"), inducted: sumKey("inducted"),
      inspections: sumKey("inspections"), internalAudit: sumKey("internalAudit"),
      externalAudit: sumKey("externalAudit"), drills: sumKey("drills"),
      wasteGen: sumKey("wasteGen"), wasteRec: sumKey("wasteRec"), tbt: sumKey("tbt"),
    };
  }, [data, sites]);

  const sum = (a) => a.reduce((x, y) => x + y, 0);
  const last = (a) => a[a.length - 1] || 0;
  const prev = (a) => a[a.length - 2] || 0;
  const pct = (a, b) => (b ? Math.round(((a - b) / b) * 100) : 0);

  const ltiTotal = sum(totals.lti);
  const mhTotal = sum(totals.manhours);
  // Industry formulas (client spec):
  // LTIF Rate = LTI × 1,000,000 / Total Manhours
  // LTISR     = Workdays Lost × 1,000,000 / Total Manhours
  // Legacy recordable-rate helper retained for historical calculations.
  const safeRate = (num, mh, factor) => {
    if (!mh || !isFinite(mh) || mh <= 0) return 0;
    const v = (num * factor) / mh;
    if (!isFinite(v) || isNaN(v)) return 0;
    return +v.toFixed(2);
  };
  const recordable = sum(totals.fac) + sum(totals.mtc) + ltiTotal;
  const trir = safeRate(recordable, mhTotal, 200_000);
  const ltifr = safeRate(ltiTotal, mhTotal, 1_000_000);
  const ltisr = safeRate(sum(totals.workdaysLost), mhTotal, 1_000_000);

  const totalIncidents = sum(totals.incidents);
  const activeConstructionSites = data.settings.sites.filter(s => typeof s === "object" ? s.status === "Active" : true).length;
  const totalEquipmentTools = data.equipmentTools?.length || 0;
  const checklistsCompletedToday = (data.dailyChecklists || []).filter((c) => c.date === "2026-05-30").length;
  const pendingInspections = (data.inspections || []).filter((i) => i.status === "Open" || i.status === "In Progress").length;
  const safetyObs = sum(totals.obsRaised);
  const openActions = data.actions.filter(a => a.status === "Open" || a.status === "In Progress" || a.status === "Overdue").length;
  const closedActions = data.actions.filter(a => a.status === "Closed").length;
  const monthlyAuditScore = data.audits.length ? Math.round(data.audits.reduce((a, b) => a + Number(b.score || 0), 0) / data.audits.length) : 0;
  const highRiskFindings = data.observations.filter(o => o.riskLevel === "High" || o.riskLevel === "Critical").length;
  const overdueActionCount = data.actions.filter(a => a.status === "Overdue").length;
  const weeklyInspectionCompletion = data.inspections.length ? Math.round((data.inspections.filter(i => i.status === "Closed").length / data.inspections.length) * 100) : 0;
  const dailyChecklistStatus = data.dailyChecklists?.length ? Math.round((data.dailyChecklists.filter(c => c.status === "Closed").length / data.dailyChecklists.length) * 100) : 0;
  const trainingTotal = data.trainings.length;
  const trainingValid = data.trainings.filter(t => t.status === "Valid").length;
  const trainingCompliance = trainingTotal ? Math.round((trainingValid / trainingTotal) * 100) : (sum(totals.training) > 0 ? 92 : 0);
  const ptwActive = data.ptws.filter(p => p.status === "Approved" || p.status === "In Progress").length;

  // Site stats
  const siteList = useMemo(() => {
    const allSites = data.settings.sites || [];
    return filters.region === "All Regions"
      ? allSites
      : allSites.filter((s) => typeof s !== "object" || s.region === filters.region);
  }, [data.settings.sites, filters.region]);
  const totalSites = siteList.length;
  const activeCount = siteList.filter(s => typeof s === "object" ? s.status === "Active" : true).length;
  const inactiveCount = totalSites - activeCount;

  const siteStats = useMemo(() => siteList.map((s) => {
    const sn = typeof s === "object" ? s.number : s;
    const inactive = typeof s === "object" && s.status === "Inactive";
    const incidents = data.incidents.filter(r => r.site === sn).length;
    const overdue = data.actions.filter(r => r.site === sn && r.status === "Overdue").length;
    const openNCR = data.ncrs.filter(r => r.site === sn && (r.status === "Open" || r.status === "Overdue")).length;
    const openSOR = data.observations.filter(r => r.site === sn && r.sorId && r.status === "Open").length;
    const score = incidents * 2 + openNCR * 3 + overdue * 2;
    return { site: sn, region: s.region || "—", incidents, overdue, openNCR, openSOR, score, inactive };
  }), [data, siteList]);

  const highRisk = siteStats.filter(s => s.score > 18 && !s.inactive).length;
  const sitesWithNCR = siteStats.filter(s => s.openNCR > 0).length;
  const sitesWithSOR = siteStats.filter(s => s.openSOR > 0).length;
  const sitesWithOverdue = siteStats.filter(s => s.overdue > 0).length;

  // ===== Charts =====
  // Weekly inspection completion trend.
  const trirTrend = MONTHS.map((m, i) => ({
    month: m,
    Completion: totals.weeklyCompletion?.[i] || 0,
  }));

  // Incident classification donut
  const fac = sum(totals.fac), mtc = sum(totals.mtc), nearMiss = sum(totals.nearMiss);
  const classifTotal = fac + mtc + ltiTotal + nearMiss + sum(totals.propertyDamage);
  const classDonut = [
    { name: "Medical Treatment", value: mtc, color: RED },
    { name: "Lost Time Injury", value: ltiTotal, color: AMBER },
    { name: "Restricted Work", value: sum(totals.propertyDamage), color: BLUE },
    { name: "First Aid", value: fac, color: GREEN },
    { name: "Near Miss", value: nearMiss, color: NAVY },
  ].filter(d => d.value > 0);

  // Incidents by location (top 5 sites with most incidents — fallback to top 5 sites by index)
  const incidentsByLocation = useMemo(() => {
    const top = [...siteStats].sort((a, b) => b.incidents - a.incidents).slice(0, 5);
    return top.map((s, i) => ({
      site: s.site,
      incidents: s.incidents,
      color: [RED, AMBER, "#FBBF24", GREEN, NAVY][i],
    }));
  }, [siteStats]);

  // Risk heatmap 5x5 (Likelihood x Consequence) — populate from incidents severity/risk
  const heatmapMatrix = useMemo(() => {
    const m = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => 0));
    const sevMap = { Low: 0, Medium: 1, High: 2, Critical: 4 };
    data.incidents.forEach((r) => {
      // pseudo-likelihood from classification + consequence from severity
      const cIdx = (sevMap[r.severity] ?? 1);
      const lIdx = ({ "Near Miss": 0, "Property Damage": 1, FAC: 2, MTC: 3, LTI: 4 }[r.classification] ?? 1);
      m[lIdx][Math.min(cIdx, 4)] += 1;
    });
    return m;
  }, [data.incidents]);

  // Inspections donut (Compliant/Minor/Major/Critical from risk levels)
  const inspByRisk = ["Low", "Medium", "High", "Critical"].map((r, i) => ({
    name: r === "Low" ? "Compliant" : r,
    value: data.inspections.filter(x => x.risk === r).length,
    color: [GREEN, AMBER, "#F97316", RED][i],
  }));
  const inspTotal = inspByRisk.reduce((a, b) => a + b.value, 0);

  // PTW status donut
  const ptwData = ["Approved", "In Progress", "Closed", "Expired", "Rejected"].map((s, i) => ({
    name: s,
    value: data.ptws.filter(p => p.status === s).length,
    color: [GREEN, BLUE, GRAY, AMBER, RED][i],
  }));
  const ptwActiveCount = ptwData.find(p => p.name === "Approved")?.value || 0;

  // Environmental performance trends
  const envTrend = MONTHS.map((m, i) => ({
    month: m,
    co2: Math.round((totals.manhours[i] || 0) / 1000 * 1.2),
    waste: +(totals.wasteGen[i] || 0).toFixed(1),
    energy: Math.round((totals.manhours[i] || 0) / 100 * 0.8),
  }));
  const co2Total = envTrend.reduce((a, b) => a + b.co2, 0);
  const wasteTotal = +totals.wasteGen.reduce((a, b) => a + b, 0).toFixed(0);
  const energyTotal = envTrend.reduce((a, b) => a + b.energy, 0);

  const manhoursIncidentTrend = MONTHS.map((m, i) => ({
    month: m,
    Manhours: Math.round((totals.manhours[i] || 0) / 1000),
    Incidents: totals.incidents[i] || 0,
    "Near Misses": totals.nearMiss[i] || 0,
  }));

  const observationCategoryData = ["Housekeeping", "PPE", "Behavior", "Equipment", "Procedure"].map((category, i) => ({
    category,
    count: data.observations.filter((o) => o.category === category).length,
    color: [GREEN, BLUE, AMBER, PURPLE, NAVY][i],
  }));

  const actionStatusData = ["Open", "In Progress", "Closed", "Overdue"].map((status, i) => ({
    status,
    value: data.actions.filter((a) => a.status === status).length,
    color: [AMBER, BLUE, GREEN, RED][i],
  }));

  const ncrStatusData = ["Open", "In Progress", "Closed", "Overdue"].map((status, i) => ({
    status,
    value: data.ncrs.filter((n) => n.status === status).length,
    color: [AMBER, BLUE, GREEN, RED][i],
  }));

  const auditFindingsData = data.audits.map((audit) => ({
    site: audit.site,
    Findings: audit.findings || 0,
    Score: audit.score || 0,
  }));

  const riskLevelTrend = MONTHS.map((month, idx) => {
    const incidentRisk = data.incidents.filter((r) => r.date && new Date(r.date).getMonth() === idx);
    const inspectionRisk = data.inspections.filter((r) => r.date && new Date(r.date).getMonth() === idx);
    return {
      month,
      High: incidentRisk.filter((r) => r.severity === "High" || r.severity === "Critical").length + inspectionRisk.filter((r) => r.risk === "High" || r.risk === "Critical").length,
      Medium: incidentRisk.filter((r) => r.severity === "Medium").length + inspectionRisk.filter((r) => r.risk === "Medium").length,
    };
  });

  // Recent incidents
  const recent = [...data.incidents].slice(-5).reverse();
  const overdueActions = [...data.actions].filter(a => a.status === "Overdue" || a.status === "Open").slice(0, 5);

  // Site performance (top 5 sites by score for "world map" style legend)
  const sitePerf = [...siteStats].slice(0, 6).map((s) => ({
    site: s.site,
    rating: s.score > 18 ? "At Risk" : s.score > 8 ? "Fair" : "Good",
    color: s.score > 18 ? RED : s.score > 8 ? AMBER : GREEN,
  }));

  return (
    <div className="space-y-5" id="executive-dashboard" data-testid="executive-dashboard">
      {/* Header strip with title */}
      <div className="flex items-end justify-between flex-wrap gap-3 -mt-2">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="font-heading font-bold text-2xl tracking-tight text-[#082B4D]">Executive Dashboard</h1>
            <span className="cmd-pill" style={{ background: "#FEF3C7", color: "#854D0E" }}>★ Featured</span>
          </div>
          <div className="text-sm text-slate-500 mt-0.5">Muhammad Waqas Younas - Construction HSE & Internal Audit Performance</div>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2">
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#18B84F] animate-pulse" /> Live</span>
          <span>•</span>
          <span className="font-mono">{filters.site} · {filters.region}</span>
        </div>
      </div>

      {/* Top KPI strip — icon-led cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="kpi-grid">
        <BigKpi icon={Shield} iconBg="#DCFCE7" iconColor={GREEN} label="Active Construction Sites" value={activeConstructionSites} delta={0} />
        <BigKpi icon={Zap} iconBg="#DBEAFE" iconColor={BLUE} label="Total Equipment & Power Tools" value={totalEquipmentTools} delta={6} />
        <BigKpi icon={ClipboardList} iconBg="#DCFCE7" iconColor={GREEN} label="Checklists Completed Today" value={checklistsCompletedToday} delta={8} good />
        <BigKpi icon={Clock4} iconBg="#FEF3C7" iconColor={AMBER} label="Pending Inspections" value={pendingInspections} delta={-10} good />
        <BigKpi icon={MessageSquareWarning} iconBg="#DBEAFE" iconColor={BLUE} label="Open Findings / Observations" value={data.observations.filter(o => o.status !== "Closed").length} delta={-15} good />
        <BigKpi icon={ClipboardList} iconBg="#FEF3C7" iconColor={AMBER} label="Corrective Actions Open" value={openActions} delta={-13} good />
        <BigKpi icon={Shield} iconBg="#DCFCE7" iconColor={GREEN} label="Corrective Actions Closed" value={closedActions} delta={12} good />
        <BigKpi icon={ShieldCheck} iconBg="#EDE9FE" iconColor={PURPLE} label="Monthly Internal Audit Score" value={`${monthlyAuditScore}%`} delta={4} good />
      </div>

      {/* Site overview stats — small chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <MiniStat icon={ShieldAlert} label="High Risk Findings" value={highRiskFindings} tone="critical" />
        <MiniStat icon={CalendarX} label="Overdue Actions" value={overdueActionCount} tone="warning" />
        <MiniStat icon={ClipboardCheck} label="Weekly Completion %" value={`${weeklyInspectionCompletion}%`} tone="safe" />
        <MiniStat icon={ClipboardList} label="Daily Checklist Status" value={`${dailyChecklistStatus}%`} tone="safe" />
        <MiniStat icon={Building2} label="Active Sites" value={activeCount} tone="info" />
        <MiniStat icon={Eye} label="Open Finding Sites" value={sitesWithSOR} tone="warning" />
        <MiniStat icon={AlertTriangle} label="Construction Incidents" value={totalIncidents} tone="critical" />
      </div>

      {/* Row 2 - charts: completion trend / classification / by location / heatmap */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <PanelCard title="Weekly Inspection Completion Trend" rightSlot={<TimeSelector />}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trirTrend}>
              <defs>
                <linearGradient id="trir" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={GREEN} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<TT />} />
              <Area type="monotone" dataKey="Completion" stroke={GREEN} strokeWidth={2.5} fill="url(#trir)" dot={{ r: 3, fill: GREEN }} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#18B84F]" /> Completion trend
          </div>
        </PanelCard>

        <PanelCard title="Construction Incident Classification">
          <div className="flex items-center gap-3">
            <div className="relative w-[140px] h-[140px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={classDonut} dataKey="value" innerRadius={42} outerRadius={66} paddingAngle={2}>
                    {classDonut.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<TT />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-heading font-bold text-2xl tabular text-[#082B4D] leading-none">{classifTotal}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">Total</div>
              </div>
            </div>
            <ul className="flex-1 space-y-1.5 text-[11px] min-w-0">
              {classDonut.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                  <span className="text-slate-700 truncate">{d.name}</span>
                  <span className="ml-auto font-bold tabular text-[#082B4D]">{d.value} <span className="text-slate-400 font-medium">({classifTotal ? Math.round((d.value / classifTotal) * 100) : 0}%)</span></span>
                </li>
              ))}
              {classDonut.length === 0 && <li className="text-slate-400 italic">No incidents</li>}
            </ul>
          </div>
        </PanelCard>

        <PanelCard title="Incidents by Location" rightSlot={<TimeSelector text="All Locations" />}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={incidentsByLocation} layout="vertical" margin={{ left: 12, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis type="number" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="site" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} width={70} />
              <Tooltip content={<TT />} />
              <Bar dataKey="incidents" radius={[0, 6, 6, 0]} barSize={18}>
                {incidentsByLocation.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold text-center mt-1">Number of Incidents</div>
        </PanelCard>

        <PanelCard title="Risk Heatmap">
          <RiskHeatmap matrix={heatmapMatrix} />
        </PanelCard>
      </div>

      {/* Row 3 — 5 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <PanelCard title="Inspections & Findings">
          <div className="flex items-center gap-3">
            <div className="relative w-[120px] h-[120px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={inspByRisk.filter(x => x.value > 0)} dataKey="value" innerRadius={36} outerRadius={56} paddingAngle={2}>
                    {inspByRisk.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<TT />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-heading font-bold text-xl tabular text-[#082B4D] leading-none">{inspTotal}</div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">Inspections</div>
              </div>
            </div>
            <ul className="flex-1 space-y-1.5 text-[11px]">
              {inspByRisk.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: d.color }} />
                  <span className="text-slate-600">{d.name}</span>
                  <span className="ml-auto font-bold tabular text-[#082B4D]">{d.value} <span className="text-slate-400 font-medium">({inspTotal ? Math.round((d.value / inspTotal) * 100) : 0}%)</span></span>
                </li>
              ))}
            </ul>
          </div>
          <button onClick={() => nav("/inspections")} className="mt-3 text-[11px] text-[#2563EB] font-semibold hover:underline">Findings this month: {inspByRisk.reduce((a, b) => a + b.value, 0)}</button>
        </PanelCard>

        <PanelCard title="Permit to Work Status">
          <div className="flex items-center gap-3">
            <div className="relative w-[120px] h-[120px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={ptwData.filter(x => x.value > 0)} dataKey="value" innerRadius={36} outerRadius={56} paddingAngle={2}>
                    {ptwData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<TT />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-heading font-bold text-xl tabular text-[#082B4D] leading-none">{ptwActiveCount}</div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">Active PTW</div>
              </div>
            </div>
            <ul className="flex-1 space-y-1.5 text-[11px]">
              {ptwData.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: d.color }} />
                  <span className="text-slate-600">{d.name}</span>
                  <span className="ml-auto font-bold tabular text-[#082B4D]">{d.value}</span>
                </li>
              ))}
            </ul>
          </div>
          <button onClick={() => nav("/daily-safety-checklist")} className="mt-3 text-[11px] text-[#2563EB] font-semibold hover:underline flex items-center gap-1">View daily checklist <ArrowRight className="w-3 h-3" /></button>
        </PanelCard>

        <PanelCard title="Daily Checklist Completion">
          <div className="flex flex-col items-center justify-center">
            <div className="relative w-[160px] h-[160px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart innerRadius="74%" outerRadius="100%" data={[{ name: "Completion", value: dailyChecklistStatus, fill: GREEN }]} startAngle={90} endAngle={-270}>
                  <RadialBar background={{ fill: "#F1F5F9" }} dataKey="value" cornerRadius={20} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-heading font-bold text-3xl tabular text-[#082B4D] leading-none">{dailyChecklistStatus}%</div>
                <div className="text-[10px] uppercase tracking-wider text-[#18B84F] font-bold mt-1">Compliant</div>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#18B84F] font-bold mt-2">Target: {data.settings.targets.dailyChecklistCompletion}%</div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            <Sub label="Total Employees" value={trainingTotal || data.settings.users.length} />
            <Sub label="Compliant" value={trainingValid} />
            <Sub label="Non-Compliant" value={Math.max(trainingTotal - trainingValid, 0)} tone="critical" />
          </div>
        </PanelCard>

        <PanelCard title="Environmental Performance" subtitle="Live indicators">
          <div className="space-y-2.5">
            <EnvRow icon={Wind} color="#2563EB" label="CO₂ Emissions (tCO₂e)" value={co2Total} delta={-12} data={envTrend.map(t => ({ v: t.co2 }))} />
            <EnvRow icon={Recycle} color="#18B84F" label="Waste Generated (T)" value={wasteTotal} delta={-8} data={envTrend.map(t => ({ v: t.waste }))} />
            <EnvRow icon={Zap} color="#F59E0B" label="Energy Consumption (MWh)" value={energyTotal} delta={5} negDelta data={envTrend.map(t => ({ v: t.energy }))} />
          </div>
          <button onClick={() => nav("/reports")} className="mt-3 text-[11px] text-[#2563EB] font-semibold hover:underline flex items-center gap-1">Export waste data <ArrowRight className="w-3 h-3" /></button>
        </PanelCard>

        <PanelCard title="Construction Team Safety" subtitle="Leading indicators">
          <div className="grid grid-cols-2 gap-3">
            <Sub label="Total Contractors" value={data.settings.contractors.length} />
            <Sub label="Contractor Employees" value={data.ptws.length * 4} />
            <Sub label="Safety Observations" value={Math.round(safetyObs * 0.5)} delta={23} good />
            <Sub label="Unsafe Acts/Conditions" value={data.observations.filter(o => o.type === "Unsafe").length} delta={-18} good />
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Open High Risk Findings</div>
            <div className="flex items-baseline gap-2">
              <div className="font-heading font-bold text-2xl tabular text-[#082B4D]">{highRiskFindings}</div>
              <span className="text-[11px] font-bold text-[#18B84F]">↑ 15%</span>
            </div>
          </div>
        </PanelCard>
      </div>

      {/* Row 4 — Recent Incidents / Top Actions / Site Performance */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <PanelCard title="Manhours vs Incidents">
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={manhoursIncidentTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<TT />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="Manhours" fill={NAVY} radius={[5, 5, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="Incidents" stroke={RED} strokeWidth={2.5} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="Near Misses" stroke={AMBER} strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </PanelCard>

        <PanelCard title="Observation Categories">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={observationCategoryData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="category" tick={{ fill: GRAY, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<TT />} />
              <Bar dataKey="count" radius={[5, 5, 0, 0]}>
                {observationCategoryData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </PanelCard>

        <PanelCard title="Action Status">
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={actionStatusData.filter((x) => x.value > 0)} dataKey="value" nameKey="status" innerRadius={58} outerRadius={88} paddingAngle={2}>
                {actionStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip content={<TT />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </PanelCard>

        <PanelCard title="Internal Audit Finding Status">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={ncrStatusData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="status" tick={{ fill: GRAY, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<TT />} />
              <Bar dataKey="value" radius={[5, 5, 0, 0]}>
                {ncrStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </PanelCard>

        <PanelCard title="Audit Findings">
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={auditFindingsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="site" tick={{ fill: GRAY, fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
              <YAxis yAxisId="left" allowDecimals={false} tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<TT />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="Findings" fill={AMBER} radius={[5, 5, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="Score" stroke={GREEN} strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </PanelCard>

        <PanelCard title="Risk Level Trend">
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={riskLevelTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<TT />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="High" stroke={RED} fill={RED} fillOpacity={0.16} strokeWidth={2.3} />
              <Area type="monotone" dataKey="Medium" stroke={AMBER} fill={AMBER} fillOpacity={0.14} strokeWidth={2.3} />
            </AreaChart>
          </ResponsiveContainer>
        </PanelCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <PanelCard title="Recent Construction Incidents" rightSlot={<button onClick={() => nav("/observations")} className="text-[11px] text-[#2563EB] font-semibold hover:underline">View findings</button>}>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                <th className="text-left py-1.5 pr-2">Date</th>
                <th className="text-left py-1.5 pr-2">Location</th>
                <th className="text-left py-1.5 pr-2">Type</th>
                <th className="text-left py-1.5 pr-2">Description</th>
                <th className="text-left py-1.5 pr-2">Severity</th>
                <th className="text-left py-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="py-2 pr-2 text-slate-500 whitespace-nowrap">{r.date}</td>
                  <td className="py-2 pr-2 text-[#082B4D] font-semibold whitespace-nowrap">{r.site}</td>
                  <td className="py-2 pr-2 text-slate-600 whitespace-nowrap">{r.classification}</td>
                  <td className="py-2 pr-2 text-slate-600 truncate max-w-[160px]">{r.description}</td>
                  <td className="py-2 pr-2"><SevPill v={r.severity} /></td>
                  <td className="py-2"><StatusPill v={r.status} /></td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-400 italic">No incidents recorded</td></tr>
              )}
            </tbody>
          </table>
        </PanelCard>

        <PanelCard title="Corrective Actions" subtitle="Overdue" rightSlot={<button onClick={() => nav("/corrective-actions")} className="text-[11px] text-[#2563EB] font-semibold hover:underline">View all</button>}>
          <ul className="space-y-2">
            {overdueActions.map((a, i) => (
              <li key={a.id} className="flex items-center gap-2.5 text-[11px]">
                <div className="w-6 h-6 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center font-bold text-[10px] shrink-0 border border-rose-100">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-700 truncate font-medium">{a.description}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{a.id} · {a.site}</div>
                </div>
                <SevPill v={a.priority} compact />
                <span className="text-[10px] uppercase tracking-wider font-bold text-rose-600 whitespace-nowrap">
                  {a.daysOverdue > 0 ? `${a.daysOverdue} days` : "Open"}
                </span>
              </li>
            ))}
            {overdueActions.length === 0 && <li className="text-slate-400 italic text-[11px] text-center py-4">No overdue actions</li>}
          </ul>
        </PanelCard>

        <PanelCard title="Site Performance" rightSlot={<button onClick={() => document.getElementById("site-map-section")?.scrollIntoView({ behavior: "smooth" })} className="text-[11px] text-[#2563EB] font-semibold hover:underline">View full map</button>}>
          <WorldStyleMap regions={data.settings.regions || []} sitePerf={sitePerf} />
          <ul className="mt-3 space-y-1 text-[11px]">
            {sitePerf.map((s, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                <span className="text-slate-700 font-medium">{s.site}</span>
                <span className="ml-auto font-bold tabular" style={{ color: s.color }}>{s.rating}</span>
              </li>
            ))}
          </ul>
        </PanelCard>
      </div>

      {/* Site Map */}
      <PanelCard id="site-map-section" title="Site Map / Site Overview" subtitle={`Click any site to filter dashboard · ${totalSites} sites total`} rightSlot={
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider font-semibold">
          <Legend2 color={GREEN} label="Safe" />
          <Legend2 color={AMBER} label="Warning" />
          <Legend2 color={RED} label="High Risk" />
          <Legend2 color="#9CA3AF" label="Inactive" />
        </div>
      }>
        <SiteMap />
      </PanelCard>
    </div>
  );
}

// ===== Helper components =====

function PanelCard({ title, subtitle, rightSlot, children, id }) {
  return (
    <div id={id} className="command-card p-4 lg:p-5">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <h3 className="font-heading font-semibold text-[14px] text-[#082B4D] flex items-center gap-1.5">
            {title}
            <AlertCircle className="w-3 h-3 text-slate-300" />
            {subtitle && <span className="text-[11px] font-medium text-slate-400 ml-1">{subtitle}</span>}
          </h3>
        </div>
        {rightSlot}
      </div>
      {children}
    </div>
  );
}

function TimeSelector({ text = "Monthly" }) {
  return (
    <button className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1 hover:text-[#082B4D]">
      {text} <ChevronRight className="w-3 h-3 rotate-90" />
    </button>
  );
}

function BigKpi({ icon: Icon, iconBg, iconColor, label, value, delta, good, spark }) {
  const TrendIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const trendColor = delta === 0 ? "text-slate-400" :
    (good ? (delta < 0 ? "text-[#18B84F]" : "text-rose-500") : (delta > 0 ? "text-[#18B84F]" : "text-rose-500"));
  const max = spark ? Math.max(...spark, 1) : 1;
  const points = spark ? spark.map((v, i) => `${(i / Math.max(spark.length - 1, 1)) * 100},${24 - (v / max) * 22}`).join(" ") : "";
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-3.5 hover:shadow-md transition-all" data-testid={`kpi-${label.toLowerCase().replace(/[^\w]+/g, "-")}-card`}>
      <div className="flex items-start gap-2.5 mb-1.5">
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: iconBg }}>
          <Icon className="w-4 h-4" style={{ color: iconColor }} strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{label}</div>
          <div className="font-heading font-bold text-[28px] leading-none mt-1.5 tabular text-[#082B4D]">{value}</div>
        </div>
        {spark && spark.length > 1 && (
          <svg width="48" height="24" viewBox="0 0 100 24" preserveAspectRatio="none" className="opacity-90 shrink-0">
            <polyline fill="none" stroke={iconColor} strokeWidth="1.6" points={points} />
          </svg>
        )}
      </div>
      <div className="flex items-center gap-1.5 text-[10px] mt-1.5">
        <span className={`flex items-center gap-0.5 font-bold ${trendColor}`}>
          <TrendIcon className="w-3 h-3" />{Math.abs(delta || 0)}%
        </span>
        <span className="text-slate-400">{delta === 0 ? "No change" : "vs Apr 2024"}</span>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, tone }) {
  const c = { safe: "#18B84F", warning: "#F59E0B", critical: "#EF4444", info: "#2563EB", neutral: "#6B7280" }[tone];
  const bg = { safe: "#DCFCE7", warning: "#FEF3C7", critical: "#FEE2E2", info: "#DBEAFE", neutral: "#F3F4F6" }[tone];
  return (
    <div className="bg-white rounded-lg border border-slate-100 px-3 py-2.5 flex items-center gap-2.5 hover:shadow-sm transition">
      <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: bg, color: c }}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold truncate">{label}</div>
        <div className="font-heading font-bold text-lg text-[#082B4D] tabular leading-tight">{value}</div>
      </div>
    </div>
  );
}

function Sub({ label, value, tone, delta, good }) {
  const c = tone === "critical" ? "#EF4444" : "#082B4D";
  const TrendIcon = delta > 0 ? TrendingUp : TrendingDown;
  const tColor = delta === undefined ? "" : (good ? (delta < 0 ? "text-[#18B84F]" : "text-rose-500") : (delta > 0 ? "text-[#18B84F]" : "text-rose-500"));
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{label}</div>
      <div className="font-heading font-bold text-lg tabular leading-tight mt-0.5" style={{ color: c }}>{value}</div>
      {delta !== undefined && (
        <div className={`text-[10px] font-bold flex items-center gap-0.5 ${tColor}`}>
          <TrendIcon className="w-2.5 h-2.5" /> {Math.abs(delta)}%
        </div>
      )}
    </div>
  );
}

function EnvRow({ icon: Icon, color, label, value, delta, negDelta, data }) {
  const isGood = negDelta ? delta < 0 : true;
  const goodColor = (delta < 0) === isGood ? "#18B84F" : "#EF4444";
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{label}</div>
        <div className="flex items-baseline gap-1.5">
          <div className="font-heading font-bold text-base tabular text-[#082B4D]">{value}</div>
          <span className="text-[10px] font-bold" style={{ color: goodColor }}>
            {delta < 0 ? "↓" : "↑"} {Math.abs(delta)}% <span className="text-slate-400 font-medium">vs Apr 2024</span>
          </span>
        </div>
      </div>
      <svg width="48" height="20" viewBox="0 0 100 20" preserveAspectRatio="none" className="shrink-0">
        <polyline fill="none" stroke={color} strokeWidth="1.6"
          points={data.map((d, i) => `${(i / Math.max(data.length - 1, 1)) * 100},${20 - (d.v / Math.max(...data.map(x => x.v), 1)) * 18}`).join(" ")} />
      </svg>
    </div>
  );
}

function RiskHeatmap({ matrix }) {
  const labelsL = ["Almost Certain", "Likely", "Possible", "Unlikely", "Rare"];
  const labelsC = ["Insignificant", "Minor", "Moderate", "Major", "Severe"];
  const max = Math.max(...matrix.flat(), 1);
  const cellColor = (v, l, c) => {
    // High consequence + high likelihood = red, low x low = green
    const score = (l + c);
    if (v === 0) {
      if (score >= 7) return "#FCA5A5";
      if (score >= 5) return "#FCD34D";
      if (score >= 3) return "#86EFAC";
      return "#86EFAC";
    }
    const intensity = v / max;
    if (score >= 7) return `rgba(239,68,68,${0.7 + intensity * 0.3})`;
    if (score >= 5) return `rgba(251,191,36,${0.7 + intensity * 0.3})`;
    return `rgba(34,197,94,${0.6 + intensity * 0.4})`;
  };
  return (
    <div className="text-[10px]">
      <div className="grid grid-cols-[80px_repeat(5,1fr)] gap-1">
        <div></div>
        {labelsC.map((c, i) => <div key={i} className="text-center text-[9px] text-slate-500 font-semibold">{i + 1}</div>)}
        {[4, 3, 2, 1, 0].map((row, ri) => (
          <React.Fragment key={ri}>
            <div className="text-[9px] text-slate-500 font-semibold flex items-center justify-end pr-1">{5 - ri} {labelsL[ri]}</div>
            {[0, 1, 2, 3, 4].map((col) => {
              const v = matrix[row][col];
              return (
                <div key={col} className="aspect-square rounded flex items-center justify-center font-bold text-[#082B4D] heat-cell"
                  style={{ background: cellColor(v, row, col) }}>
                  {v > 0 ? v : ""}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div className="grid grid-cols-[80px_repeat(5,1fr)] gap-1 mt-1">
        <div></div>
        {labelsC.map((c, i) => <div key={i} className="text-center text-[8px] text-slate-400 truncate">{c}</div>)}
      </div>
      <div className="text-center text-[9px] uppercase tracking-wider text-slate-500 font-semibold mt-2">Consequence</div>
    </div>
  );
}

function WorldStyleMap({ regions, sitePerf }) {
  // Stylized SVG continents with site dots
  return (
    <div className="relative w-full h-[140px] bg-slate-50 rounded-lg overflow-hidden border border-slate-100">
      <svg viewBox="0 0 400 200" className="w-full h-full">
        {/* simple continents */}
        <g fill="#E2E8F0" stroke="#CBD5E1" strokeWidth="0.5">
          <path d="M30 60 Q 50 40, 80 50 T 130 60 L 140 90 Q 110 110, 80 100 L 50 95 Z" />
          <path d="M150 70 Q 180 50, 220 60 T 270 80 L 270 130 Q 230 145, 200 130 L 160 110 Z" />
          <path d="M280 60 Q 310 50, 340 65 T 380 90 L 370 130 Q 330 140, 300 125 L 280 100 Z" />
          <path d="M200 145 Q 230 145, 250 160 T 240 185 L 210 180 Z" />
          <path d="M70 130 Q 90 130, 110 140 L 100 170 L 80 165 Z" />
          <path d="M310 145 Q 330 145, 345 155 L 340 175 L 320 170 Z" />
        </g>
        {/* site dots */}
        {sitePerf.map((s, i) => {
          const positions = [
            { cx: 90, cy: 75 }, { cx: 210, cy: 90 }, { cx: 320, cy: 80 },
            { cx: 220, cy: 165 }, { cx: 90, cy: 150 }, { cx: 330, cy: 155 },
          ];
          const p = positions[i % positions.length];
          return (
            <g key={i}>
              <circle cx={p.cx} cy={p.cy} r="6" fill={s.color} opacity="0.3" />
              <circle cx={p.cx} cy={p.cy} r="3" fill={s.color}>
                <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
              </circle>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function SevPill({ v, compact }) {
  if (!v) return <span className="text-slate-300">—</span>;
  const map = {
    Low: { bg: "#DCFCE7", color: "#166534" },
    Medium: { bg: "#FEF3C7", color: "#854D0E" },
    High: { bg: "#FFE4E6", color: "#9F1239" },
    Critical: { bg: "#FEE2E2", color: "#7F1D1D" },
  };
  const m = map[v] || { bg: "#F1F5F9", color: "#334155" };
  return <span className={`cmd-pill ${compact ? "text-[9px]" : ""}`} style={{ background: m.bg, color: m.color }}>{v}</span>;
}

function StatusPill({ v }) {
  if (!v) return <span className="text-slate-300">—</span>;
  const map = {
    Open: { bg: "#FEF3C7", color: "#854D0E" },
    "In Progress": { bg: "#DBEAFE", color: "#1E40AF" },
    "Under Investigation": { bg: "#DBEAFE", color: "#1E40AF" },
    Closed: { bg: "#DCFCE7", color: "#166534" },
    Overdue: { bg: "#FEE2E2", color: "#7F1D1D" },
  };
  const m = map[v] || { bg: "#F1F5F9", color: "#334155" };
  return <span className="cmd-pill" style={{ background: m.bg, color: m.color }}>{v}</span>;
}

function Legend2({ color, label }) {
  return (
    <span className="flex items-center gap-1.5 text-slate-500">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function TT({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-lg px-3 py-2 text-[11px]">
      {label && <div className="font-semibold text-[#082B4D] mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 tabular">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}:</span>
          <span className="font-semibold text-[#082B4D]">{Number(p.value).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}
